import { Liquid } from 'liquidjs';

/** Singleton engine; file system disabled (no layouts/includes). */
const engine = new Liquid({ cache: false, outputEscape: (v) => String(v) });

/** Minimal filters for dev. Add as needed. */
engine.registerFilter('upcase', (s: unknown) => String(s ?? '').toUpperCase());
engine.registerFilter('downcase', (s: unknown) => String(s ?? '').toLowerCase());
engine.registerFilter('default', (v: unknown, d: unknown) => (v === undefined ? d : v));

export async function renderTemplate(tpl: string, scope: Record<string, unknown>) {
  const tplObj = engine.parse(tpl);
  return engine.render(tplObj, scope);
}

/** Extract top-level variable name from an expression: "user.name[0]" -> "user" */
export function topLevelOf(expr: string): string | null {
  // strip filters & trailing ops
  const base = expr.split('|')[0]!.trim();
  // remove bracket segments, split by dot, take first identifier
  const cleaned = base.replace(/\[['"][^'"]+['"]\]/g, '').replace(/\[\d+\]/g, '');
  const m = cleaned.match(/^[A-Za-z_]\w*/);
  if (!m) return null;
  const name = m[0]!;
  if (['true', 'false', 'nil', 'null', 'blank', 'empty'].includes(name)) return null;
  return name;
}

/** Find loop variables to exclude (e.g., `{% for item in list %}` => exclude `item`) */
export function scanLoopVars(tpl: string): Set<string> {
  const out = new Set<string>();
  const re = /{%-?\s*for\s+([A-Za-z_]\w*)\s+in\s+[^%]+?-?%}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(tpl))) out.add(m[1]!);
  return out;
}

/** Scan a single string template for referenced top-level vars. */
export function scanVariablesInString(tpl: string): Set<string> {
  const set = new Set<string>();
  const exclude = scanLoopVars(tpl);
  // output tags: {{ expr }}
  const reOut = /{{-?\s*([^}]+?)\s*-?}}/g;
  let m: RegExpExecArray | null;
  while ((m = reOut.exec(tpl))) {
    const expr = m[1]!;
    const top = topLevelOf(expr);
    if (top && !exclude.has(top)) set.add(top);
  }
  // for/in expressions: {% for item in list.path %}
  const reFor = /{%-?\s*for\s+[A-Za-z_]\w*\s+in\s+([^%|} ]+)/g;
  while ((m = reFor.exec(tpl))) {
    const top = topLevelOf(m[1]!);
    if (top) set.add(top);
  }
  return set;
}

/** Walk any value and collect referenced top-level vars from strings. */
export function scanVariablesInObject(value: unknown): Set<string> {
  const out = new Set<string>();
  const visit = (v: unknown) => {
    if (typeof v === 'string') {
      for (const t of scanVariablesInString(v)) out.add(t);
    } else if (Array.isArray(v)) {
      v.forEach(visit);
    } else if (v && typeof v === 'object') {
      Object.values(v as Record<string, unknown>).forEach(visit);
    }
  };
  visit(value);
  return out;
}

